import React, { useEffect, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Label } from "../../ui/label";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useAuthenticate, Facility } from "../../../lib/api";

/**
 * FacilitySelector component
 * 
 * Dropdown for selecting a facility from the client's available facilities
 */
interface FacilitySelectorProps {
  className?: string;
  isTarget?: boolean;
  label?: string;
  client?: any;
}

const FacilitySelector: React.FC<FacilitySelectorProps> = ({ 
  className,
  isTarget = false,
  label = "Facility",
  client
}) => {
  const { 
    selectedFacilityId, 
    setSelectedFacilityId, 
    clearSelectedChecklists,
    selectedClient,
    targetFacilityId,
    setTargetFacilityId,
    targetClient
  } = useWorkflowStore();
  
  // Use the appropriate client and facility ID based on isTarget
  const currentClient = client || (isTarget ? targetClient : selectedClient);
  const currentFacilityId = isTarget ? targetFacilityId : selectedFacilityId;
  const setFacilityId = isTarget ? setTargetFacilityId : setSelectedFacilityId;
  
  // Get facilities from the authentication response
  const { data: authData, isLoading } = useAuthenticate(currentClient);
  const facilities = authData?.facilities || [];
  
  // No auto-selection - user must explicitly select a facility

  // Reset facility selection when client changes
  useEffect(() => {
    if (currentClient) {
      setFacilityId(null);
      // Only clear checklists for source selection
      if (!isTarget) {
        clearSelectedChecklists();
      }
    }
  }, [currentClient, setFacilityId, clearSelectedChecklists, isTarget]);

  // Handle facility selection change
  const handleFacilityChange = (facilityId: string) => {
    // Clear selected checklists when facility changes (only for source)
    if (!isTarget && facilityId !== currentFacilityId) {
      clearSelectedChecklists();
    }
    
    setFacilityId(facilityId);
  };

  return (
    <div className={className}>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor={isTarget ? "target-facility" : "facility"}>{label}</Label>
        <Select
          value={currentFacilityId || undefined}
          onValueChange={handleFacilityChange}
          disabled={isLoading || !currentClient}
        >
          <SelectTrigger id={isTarget ? "target-facility" : "facility"} className="w-full">
            <SelectValue placeholder={isTarget ? "Select facility" : "Select source facility"} />
          </SelectTrigger>
          <SelectContent>
            {isLoading ? (
              <SelectItem value="loading" disabled>
                Loading facilities...
              </SelectItem>
            ) : !currentClient ? (
              <SelectItem value="no-client" disabled>
                Select a client first
              </SelectItem>
            ) : facilities.length === 0 ? (
              <SelectItem value="no-facilities" disabled>
                No facilities available
              </SelectItem>
            ) : (
              facilities.map((facility) => (
                <SelectItem key={facility.id} value={facility.id}>
                  {facility.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default FacilitySelector;
